/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {normalizeEntity} from '@ion/core/src/entities/EntityUtils';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {UserContext} from '@ion/core/src/services/MicroServices';
import {CRUDPermissions, User} from '@ion/core/src/types/entities';
import {UserEditor} from '@ion/dls/src/editors/UserEditor';
import {ApplicationKeys} from '../../../types/application';
import {auditAdminUser, editAdminUser, editUser} from './state';
import {useAuthenticatedUser} from './useAuthenticatedUser';

export interface EditUserEditorProps {
  getRouteLabel: (route: string, useTitleCase?: boolean) => string;
  defaultPageId: string;
  isCtcAdmin?: boolean;
  showJobNotificationsSetup?: boolean;
  watchCreatedJobsDefault?: boolean;
  onWatchCreatedJobsChange?: (on: boolean) => void;
  context?: UserContext;
  includeApplications?: boolean;
  includeAddresses?: boolean;
  includeContacts?: boolean;
  multipleAddress?: boolean;
  onEdit?: Function;
  includeGroupProperties?: boolean;
  usersPermissions?: CRUDPermissions;
  isViewDialog?: boolean;
}

/**
 * @EditUserEditor
 *
 * The EditUserEditor component provides the editor which is used for editing the current
 * user (i.e. the logged in user) as well as for allowing the current user to edit other
 * users. This component must be accessible throughout the application and not just from
 * the Admin Users Page; thus, it is only to be added once, and should only be added in
 * the LayoutComponent.
 */
export const EditUserEditor = ({
  getRouteLabel,
  defaultPageId,
  isCtcAdmin,
  showJobNotificationsSetup = false,
  watchCreatedJobsDefault = false,
  onWatchCreatedJobsChange,
  context,
  includeApplications,
  includeAddresses,
  includeContacts,
  usersPermissions,
  isViewDialog = false,
  includeGroupProperties = true,
  multipleAddress = false
}: EditUserEditorProps) => {
  const dispatch      = useDispatch();

  const currentUser   = useAuthenticatedUser();
  const isCurrentUser = useSelector(({adminUsers: {edit}}) => edit.isCurrentUser);
  const {
    selected: selectedUser,
    loading,
    isEditing: showEditUser,
    isView : showViewUser
  }                                          = useSelector(({adminUsers: {edit}}) => edit);
  const roles                                = useSelector(({adminUsers: {roles}}) => roles);
  const {globalGroup}                        = useSelector(({globalGroup}) => globalGroup);
  const {accounts: subAccounts, accountsMap} = useSelector(({initialization}) => initialization);
  const pages                                = useSelector(({nav}) => nav.pages);

  const isView                               = showViewUser || false;
  const isAudit                              = useRef();
  const [isEdit, setIsEdit]                  = useState();

  function onEdit(editValue) {
    setIsEdit(editValue);
  };

  useEffect(() => {
    const setAudit = isAudit.current;
    if (isEdit) {
      dispatch(editAdminUser(normalizeEntity(selectedUser), true, false));
    } else if (isEdit === false) {
      dispatch(auditAdminUser(selectedUser));
    }
    setIsEdit(setAudit);
  }, [isEdit]);

  function handleSubmit(user: User) {
    if (!isViewDialog) {
      // CTC treats the user update as a full replace, and the immutable fields
      // below are not editable from a self edit; preserving them from the
      // authenticated user ensures an existing value is never cleared ...
      dispatch(editUser(isCurrentUser ? {
        ...user,
        usertype        : currentUser?.usertype,
        terminationDate : currentUser?.terminationDate
      } : user, isCurrentUser, context));
    }
  };

  if (showEditUser) {
    return (
      <UserEditor open
        isViewDialog={isView}
        isCtcAdmin={isCtcAdmin}
        showJobNotificationsSetup={showJobNotificationsSetup}
        watchCreatedJobsDefault={watchCreatedJobsDefault}
        onWatchCreatedJobsChange={onWatchCreatedJobsChange}
        context={context}
        multipleAddress={multipleAddress}
        includeApplications={includeApplications}
        includeAddresses={includeAddresses}
        includeContacts={includeContacts}
        includeGroupProperties={includeGroupProperties}
        loading={loading}
        account={!isCurrentUser ? selectedUser?.account : currentUser?.account}
        subAccounts={subAccounts}
        roles={roles}
        isCurrentUser={isCurrentUser}
        currentUserId={currentUser?.id}
        user={!isCurrentUser ? selectedUser : currentUser}
        profileProps={isCurrentUser ? {
          applicationPages : pages,
          defaultPageId    : defaultPageId,
          preferencesAPI   : UserPreferences,
          pageNameResolver : getRouteLabel,
          accountsMap
        } : undefined}
        namespace={ApplicationKeys.CTCAdmin}
        onSave={handleSubmit}
        onEdit={onEdit}
        usersPermissions={usersPermissions}
        onCancel={function() {
          dispatch(editAdminUser(selectedUser, false, false));
        }} />
    );
  }
  return null;
};

export default withTranslation([
  'contacts',
  'countries',
  'states'
])(EditUserEditor);
